import { handleErrors, URL } from "./depannageActions"
import { catchApiError, normalizeApiError } from "./apiHelpers"

// ORDRES D'ASSISTANCE (GE-57, backend/assistances/) — l'état vit dans les
// composants (onglet Assistances, panneau Dossier, modals) : chaque thunk
// résout avec le JSON, ou null en cas d'erreur (toast via catchApiError).
// `{ dansModal: true }` : pas de toast, la promesse résout
// `{ erreur: <payload normalisé> }` pour l'afficher dans le modal ouvert
// (règle hybride, src/CLAUDE.md).
const requete =
	(method, chemin, body = null, opts = {}) =>
	async (dispatch, getState) => {
		const { token } = getState().navInfo.login
		if (!token) return null
		return fetch(`${URL}api/assistances/${chemin}`, {
			method,
			headers: {
				Accept: "application/json, text/plain",
				"Content-type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			...(body ? { body: JSON.stringify(body) } : {}),
		})
			.then(handleErrors)
			.then((res) => res.json())
			.catch(async (err) => {
				if (opts.dansModal)
					return { erreur: await normalizeApiError(err) }
				return catchApiError(dispatch, null, {
					silent: !!opts.silencieux,
				})(err).then(() => null)
			})
	}

export const fetchCompteurAssistances = () =>
	requete("get", "compteur", null, { silencieux: true })
export const fetchConfigAssistances = () =>
	requete("get", "config", null, { silencieux: true })
export const fetchResumeMissions = (ids) =>
	requete("get", `resume-missions?ids=${ids.join(",")}`, null, {
		silencieux: true,
	})
export const fetchPropositions = () => requete("get", "propositions")
export const fetchAttentes = () => requete("get", "attentes")
export const fetchMessagesAClasser = () =>
	requete("get", "messages?traitement=a_classer")
// AS2 (CDC 16.10) : e-mails écartés « Hors sujet » (qui / quand / copie
// purgée) et « Rétablir » (→ à classer).
export const fetchMessagesHorsSujet = () =>
	requete("get", "messages?traitement=ignore", null, { silencieux: true })
export const retablirMessage = (id) =>
	requete("post", `messages/${id}/retablir`, {})
export const fetchDossierAssistance = (id) => requete("get", `dossiers/${id}`)
export const fetchFicheAssistance = (depId) =>
	requete("get", `fiche/${depId}`, null, { silencieux: true })
export const fetchMessageAssistance = (id) => requete("get", `messages/${id}`)
// Visionneur (09.10) : e-mail rendu (HTML nettoyé côté serveur, pour une
// iframe sandbox) ; `images` réaffiche les images distantes.
export const fetchMessageRendu = (id, images = false) =>
	requete("get", `messages/${id}/rendu${images ? "?images=1" : ""}`, null, {
		dansModal: true,
	})
// Mission complète (sérialisation du dispatch) pour la carte dépliable.
// `dansModal` : l'erreur s'affiche dans la zone dépliée, pas en toast.
export const fetchMissionAssistance = (id) =>
	requete("get", `missions/${id}`, null, { dansModal: true })

// GE-62 (décision 1a) : `adresses` = {indice d'op: POI formalisé}.
export const appliquerProposition = (id, operations, adresses = {}) =>
	requete(
		"post",
		`propositions/${id}/appliquer`,
		{ operations, adresses },
		{
			dansModal: true,
		},
	)
export const rejeterProposition = (id, motif) =>
	requete(
		"post",
		`propositions/${id}/rejeter`,
		{ motif },
		{ dansModal: true },
	)
// AS3 (CDC 16.10) : `cible` = { dossier_id } (suggestion choisie) ou
// { ref_affaire, creer? } ; une référence inconnue revient en 409
// `fields.creer` tant que la création n'est pas confirmée (erreur rendue,
// pas de toast : la ligne affiche la confirmation).
export const rattacherMessage = (id, cible) =>
	requete("post", `messages/${id}/rattacher`, cible, { dansModal: true })
export const suggestionsRattachement = (id, q) =>
	requete(
		"get",
		`messages/${id}/rattacher?q=${encodeURIComponent(q)}`,
		null,
		{ silencieux: true },
	)
export const messageHorsSujet = (id) =>
	requete("post", `messages/${id}/hors-sujet`, {})

// Aperçu d'une pièce (PDF / image de la liste blanche) : blob → URL locale
// (`blob:`), à révoquer par l'appelant. Null en cas d'erreur (toast).
export const fetchPieceApercu = (id, index) => async (dispatch, getState) => {
	const { token } = getState().navInfo.login
	return fetch(`${URL}api/assistances/messages/${id}/pj/${index}?apercu=1`, {
		headers: { Authorization: `Bearer ${token}` },
	})
		.then(handleErrors)
		.then(async (res) => window.URL.createObjectURL(await res.blob()))
		.catch((err) => catchApiError(dispatch, null)(err).then(() => null))
}

// Brut (.eml) et pièces jointes : servis par l'API authentifiée (jamais par
// /media/assistances/, bloqué par nginx) → téléchargés en blob.
// `nom` : nom connu côté front (pj.nom) — pas de parsing de
// Content-Disposition (filename*=utf-8''…, « % » → URIError).
export const telechargerMessageFichier =
	(id, index = null, nom = null) =>
	async (dispatch, getState) => {
		const { token } = getState().navInfo.login
		const chemin =
			index === null
				? `messages/${id}/brut`
				: `messages/${id}/pj/${index}`
		return fetch(`${URL}api/assistances/${chemin}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then(handleErrors)
			.then(async (res) => {
				const blob = await res.blob()
				const nomFichier =
					nom ||
					(index === null ? `message-${id}.eml` : `piece-${index}`)
				const url = window.URL.createObjectURL(blob)
				const a = document.createElement("a")
				a.href = url
				a.download = nomFichier
				document.body.appendChild(a)
				a.click()
				a.remove()
				window.URL.revokeObjectURL(url)
				return true
			})
			.catch((err) => catchApiError(dispatch, null)(err).then(() => null))
	}
